import { useRef, useState } from "react";
import { Upload, FileText, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  parseUserFile,
  ACCEPTED_FILE_TYPES,
  ACCEPTED_HELP_TEXT,
  UNSUPPORTED_MESSAGE,
  type ParsedFile,
} from "@/lib/file-parsing";

interface Props {
  onParsed: (parsed: ParsedFile, raw: File) => void;
  label?: string;
  disabled?: boolean;
}

export const FileDropzone = ({ onParsed, label = "Drop a file or click to upload", disabled }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(false);
  const [current, setCurrent] = useState<string | null>(null);

  const handleFiles = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    setBusy(true);
    setCurrent(file.name);
    try {
      const parsed = await parseUserFile(file);
      onParsed(parsed, file);
      toast.success(`Loaded ${file.name}`);
    } catch (e: any) {
      const msg = e?.message ?? UNSUPPORTED_MESSAGE;
      toast.error(msg);
      setCurrent(null);
    } finally {
      setBusy(false);
      setActive(false);
    }
  };

  return (
    <div className="space-y-2">
      <div
        onClick={() => !disabled && !busy && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setActive(true); }}
        onDragLeave={() => setActive(false)}
        onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
        className={`relative cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
          active ? "border-primary bg-primary/5" : "border-muted-foreground/30"
        } ${disabled ? "opacity-50 cursor-not-allowed" : "hover:border-primary/50"}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_FILE_TYPES}
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
          disabled={disabled || busy}
        />
        <div className="flex flex-col items-center gap-2">
          {busy ? (
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          ) : current ? (
            <FileText className="h-8 w-8 text-primary" />
          ) : (
            <Upload className="h-8 w-8 text-muted-foreground" />
          )}
          <p className="text-sm font-medium">{current ?? label}</p>
          <p className="text-xs text-muted-foreground">{ACCEPTED_HELP_TEXT}</p>
          {current && !busy && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); setCurrent(null); }}
              className="mt-1"
            >
              <X className="h-3 w-3 mr-1" /> Clear
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
